import { type OrgRole, orgRoles } from "@archinventory/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { UserPlus, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Page } from "../../components/page";
import { Button } from "../../components/ui/button";
import { Dialog } from "../../components/ui/dialog";
import { Input, Label, Select } from "../../components/ui/input";
import { Table, TBody, Td, THead, Th, Tr } from "../../components/ui/table";
import { api, qk, unwrap } from "../../lib/api";
import { toastError, useMe } from "../../lib/hooks";
import { formatDate } from "../../lib/utils";

export const Route = createFileRoute("/_app/members")({ component: Members });

function Members() {
  const me = useMe();
  const qc = useQueryClient();
  const isOwner = me.activeOrg?.role === "owner";
  const [inviting, setInviting] = useState(false);
  const members = useQuery({ queryKey: qk.members, queryFn: () => unwrap(api.org.members.$get()) });
  const invitations = useQuery({
    queryKey: qk.invitations,
    queryFn: () => unwrap(api.org.invitations.$get()),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: qk.org });

  const invite = useMutation({
    mutationFn: (v: { email: string; role: OrgRole }) =>
      unwrap(api.org.invitations.$post({ json: v })),
    onSuccess: async (r) => {
      setInviting(false);
      toast.success(`Invitation sent to ${r.invitation.email}`);
      await refresh();
    },
    onError: toastError,
  });
  const setRole = useMutation({
    mutationFn: (v: { memberId: string; role: OrgRole }) =>
      unwrap(
        api.org.members[":memberId"].$patch({
          param: { memberId: v.memberId },
          json: { role: v.role },
        }),
      ),
    onSuccess: refresh,
    onError: toastError,
  });
  const remove = useMutation({
    mutationFn: (memberId: string) =>
      unwrap(api.org.members[":memberId"].$delete({ param: { memberId } })),
    onSuccess: refresh,
    onError: toastError,
  });
  const cancel = useMutation({
    mutationFn: (id: string) => unwrap(api.org.invitations[":id"].$delete({ param: { id } })),
    onSuccess: refresh,
    onError: toastError,
  });

  return (
    <Page
      title="Members"
      actions={
        <Button onClick={() => setInviting(true)}>
          <UserPlus /> Invite
        </Button>
      }
    >
      <Table>
        <THead>
          <Tr>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Role</Th>
            <Th className="w-12" />
          </Tr>
        </THead>
        <TBody>
          {members.data?.members.map((m) => (
            <Tr key={m.id}>
              <Td className="font-medium">{m.name}</Td>
              <Td className="text-muted-foreground">{m.email}</Td>
              <Td>
                <Select
                  aria-label={`Role of ${m.name}`}
                  className="w-32"
                  value={m.role}
                  disabled={m.userId === me.user.id || (!isOwner && m.role === "owner")}
                  onChange={(e) =>
                    setRole.mutate({ memberId: m.id, role: e.target.value as OrgRole })
                  }
                >
                  {orgRoles
                    .filter((r) => isOwner || r !== "owner" || m.role === "owner")
                    .map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                </Select>
              </Td>
              <Td>
                {m.userId !== me.user.id && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Remove"
                    onClick={() => confirm(`Remove ${m.name}?`) && remove.mutate(m.id)}
                  >
                    <X />
                  </Button>
                )}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
      {!!invitations.data?.invitations.length && (
        <>
          <h2 className="text-sm font-medium text-muted-foreground">Pending invitations</h2>
          <Table>
            <TBody>
              {invitations.data.invitations.map((i) => (
                <Tr key={i.id}>
                  <Td>{i.email}</Td>
                  <Td>{i.role}</Td>
                  <Td className="text-muted-foreground">expires {formatDate(i.expiresAt)}</Td>
                  <Td className="w-12">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Cancel invitation"
                      onClick={() => cancel.mutate(i.id)}
                    >
                      <X />
                    </Button>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </>
      )}
      <Dialog open={inviting} onClose={() => setInviting(false)} title="Invite a member">
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            invite.mutate({
              email: String(f.get("email")),
              role: String(f.get("role")) as OrgRole,
            });
          }}
        >
          <Label htmlFor="i-email">Email</Label>
          <Input id="i-email" name="email" type="email" required />
          <Label htmlFor="i-role">Role</Label>
          <Select id="i-role" name="role" defaultValue="member">
            <option value="member">member — capture, edit, export</option>
            <option value="admin">admin — also manage clients, projects, members</option>
            {isOwner && <option value="owner">owner</option>}
          </Select>
          <Button type="submit" disabled={invite.isPending}>
            Send invitation
          </Button>
        </form>
      </Dialog>
    </Page>
  );
}
